import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type SortingState,
} from '@tanstack/react-table';
import { tenantApi } from '../../api/endpoints/tenant';
import { useAuth } from '../../hooks/useAuth';
import { Gate } from '../permissions/Gate';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ContentLoading } from '../shared/ContentLoading';
import { DataTableToolbar } from '../shared/DataTableToolbar';
import { DataTablePagination } from '../shared/DataTablePagination';
import { SortableHeader } from '../shared/SortableHeader';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { globalFilterFn } from '../../lib/dataTable';
import { RoleForm } from './RoleForm';
import { RolePermissionsView } from './RolePermissionsView';
import type { TenantRoleListItemLocal } from '../../api/types/TenantLocal';

// Los 4 roles seed (predefinidos) del tenant. Slugs reales tomados del seed del backend
// (platform-rbac-business/scripts/seed-rbac-dev.mjs): tenant_admin, retailer_admin, store_manager,
// collection_agent. OJO: el seed OMITE el flag isSystem a propósito (el GSI isSystem-roleId-index
// exige key String y isSystem es Boolean) → el backend nunca lo devuelve en el list → sin este
// fallback por slug TODOS caen a "Custom". Estos 4 son system por invariante (no editables/borrables).
const SYSTEM_ROLE_IDS = new Set([
    'tenant_admin',
    'retailer_admin',
    'store_manager',
    'collection_agent',
]);

// Un role es "Sistema" si el backend lo marca o si su slug está en el set seed (fallback documentado
// arriba). Centralizado para reusarlo en la columna Tipo y en la de Acciones.
function isSystemRole(role: TenantRoleListItemLocal): boolean {
    return role.isSystem || SYSTEM_ROLE_IDS.has(role.roleId);
}

export function RolesTable() {
    const { user } = useAuth();
    const tid = user?.tenantId ?? '';
    const [createOpen, setCreateOpen] = useState(false);
    // Rol cuyos permisos se están viendo (modal read-only). null = cerrado.
    const [viewing, setViewing] = useState<TenantRoleListItemLocal | null>(null);
    // Rol de sistema que se está clonando a custom (abre RoleForm pre-poblado). null = cerrado.
    const [cloning, setCloning] = useState<TenantRoleListItemLocal | null>(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([{ id: 'roleName', desc: false }]);
    const { data, isLoading } = useQuery({
        queryKey: ['tenant', tid, 'roles'],
        queryFn: () => tenantApi.listRoles(tid),
        enabled: !!tid,
    });

    // Columnas estables (footgun de useReactTable). roleId va como columna oculta-de-texto para que
    // el global search matchee el slug además del roleName.
    const columns = useMemo<ColumnDef<TenantRoleListItemLocal>[]>(
        () => [
            {
                accessorKey: 'roleName',
                header: ({ column }) => (
                    <span className="inline-flex items-center justify-center gap-1">
                        <SortableHeader column={column} title="Role" />
                        <HelpHint text={HELP_TEXT.role} side="right" />
                    </span>
                ),
            },
            {
                // Slug buscable (roleId) sin columna visible: se concatena al roleName en una sola
                // celda de texto para alimentar el search. Mostramos roleName + slug tenue.
                id: 'roleId',
                accessorFn: (row) => row.roleId,
                header: ({ column }) => <SortableHeader column={column} title="Slug" />,
                cell: (info) => (
                    <span className="text-muted-foreground">{info.getValue() as string}</span>
                ),
            },
            {
                id: 'tipo',
                accessorFn: (row) => (isSystemRole(row) ? 'Sistema' : 'Custom'),
                header: ({ column }) => (
                    <span className="inline-flex items-center justify-center gap-1">
                        <SortableHeader column={column} title="Tipo" />
                        <HelpHint text={HELP_TEXT.systemRole} side="right" />
                    </span>
                ),
                cell: (info) => {
                    const system = isSystemRole(info.row.original);
                    return (
                        <Badge variant={system ? 'secondary' : 'default'}>
                            {system ? 'Sistema' : 'Custom'}
                        </Badge>
                    );
                },
            },
            {
                id: 'permissions',
                accessorFn: (row) => row.permissions?.length ?? 0,
                header: ({ column }) => (
                    <span className="inline-flex items-center justify-center gap-1">
                        <SortableHeader column={column} title="Permissions" />
                        <HelpHint text={HELP_TEXT.permission} side="right" />
                    </span>
                ),
                enableGlobalFilter: false,
            },
            {
                id: 'actions',
                header: 'Acciones',
                enableSorting: false,
                enableGlobalFilter: false,
                cell: (info) => {
                    const role = info.row.original;
                    const system = isSystemRole(role);
                    return (
                        <div className="flex items-center justify-center gap-3">
                            <button
                                type="button"
                                className="text-brand-accent hover:underline"
                                onClick={() => {
                                    setViewing(role);
                                }}
                            >
                                Ver permisos
                            </button>
                            {system ? (
                                <Gate permission="tenant.role.create" scope="TENANT" scopeRef={tid}>
                                    <button
                                        type="button"
                                        className="text-brand-accent hover:underline"
                                        onClick={() => {
                                            setCloning(role);
                                        }}
                                    >
                                        Clonar a custom
                                    </button>
                                </Gate>
                            ) : (
                                <Link
                                    to="/tenant/roles/$roleId"
                                    params={{ roleId: role.roleId }}
                                    className="text-brand-accent hover:underline"
                                >
                                    Editar
                                </Link>
                            )}
                        </div>
                    );
                },
            },
        ],
        [tid],
    );

    const rows = useMemo(() => data?.items ?? [], [data]);
    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 } },
    });

    if (isLoading) return <ContentLoading />;

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate permission="tenant.role.create" scope="TENANT" scopeRef={tid}>
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear role custom
                    </Button>
                </Gate>
            </div>

            {/* Modal plano (mismo patron que UsersTable): crear role custom sin navegar de pagina. */}
            {createOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCreateOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="inline-flex items-center gap-1.5 text-lg font-semibold">
                                Crear role custom
                                <HelpHint text={HELP_TEXT.customRole} />
                            </h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCreateOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <RoleForm
                            onDone={() => {
                                setCreateOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Modal read-only: ver los permisos de un rol (Sistema o Custom). Mismo patron plano. */}
            {viewing && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setViewing(null);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                Permisos de {viewing.roleName}
                            </h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setViewing(null);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <RolePermissionsView permissions={viewing.permissions ?? []} />
                    </div>
                </div>
            )}

            {/* Modal: clonar un rol de sistema a uno custom. RoleForm pre-poblado con sus permisos. */}
            {cloning && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCloning(null);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                Clonar {cloning.roleName} a custom
                            </h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCloning(null);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <RoleForm
                            initialName={`Copia de ${cloning.roleName}`}
                            initialPermissions={cloning.permissions ?? []}
                            onDone={() => {
                                setCloning(null);
                            }}
                        />
                    </div>
                </div>
            )}
            <DataTableToolbar
                value={globalFilter}
                onChange={setGlobalFilter}
                placeholder="Buscar por nombre o slug…"
            />

            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                            {hg.headers.map((h) => (
                                <TableHead key={h.id} className="text-center">
                                    {flexRender(h.column.columnDef.header, h.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((r) => (
                        <TableRow key={r.id}>
                            {r.getVisibleCells().map((c) => (
                                <TableCell key={c.id} className="text-center align-middle">
                                    {flexRender(c.column.columnDef.cell, c.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <DataTablePagination table={table} />
        </div>
    );
}
